import { Fragment, ReactNode, useMemo } from 'react';

import {
    AccessTime,
    DeviceThermostat,
    MailOutline,
    SupervisedUserCircleOutlined,
    SwapCalls,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import CustomCampaignNavigation from 'domains/campaigns/components/CustomCampaign/CustomCampaignNavigation';
import CustomCampaignNavigationButton from 'domains/campaigns/components/CustomCampaign/CustomCampaignNavigation/CustomCampaignNavigationButton';
import { CampaignSend, CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import {
    Campaign,
    CampaignChannelLabels,
    CampaignChannels,
    Send,
    SplitActions,
    SplitTypes,
} from 'domains/campaigns/types';
import groupSendsByParentId from 'domains/campaigns/utils/groupSendsByParentId';
import inferExitSendParentIds from 'domains/campaigns/utils/inferExitSendParentIds';
import synthesizeSplitNodeIds from 'domains/campaigns/utils/synthesizeSplitNodeIds';
import { CampaignType } from 'models/enums';

import { channelIcons } from '../../../utils';

type Props = {
    campaign: Campaign;
    isPreviewOpen: boolean;
    hasNestedSplitsFlag?: boolean;
};

// ---------------------------------------------------------------------------
// Legacy (flag OFF): flat button list with no nested-split awareness.
// Original behaviour shipped before the multipleSendsConditionalSplit feature.
// ---------------------------------------------------------------------------

const ABTestNavigation = ({ campaign }: { campaign: Campaign }) => {
    const sendsCount = campaign.sends?.length ?? 0;
    const hasRemainingSend = sendsCount >= 3;

    return (
        <>
            <CustomCampaignNavigationButton icon={<SupervisedUserCircleOutlined />} label="audience" />
            <CustomCampaignNavigationButton icon={<DeviceThermostat />} label="A/B test" />
            <CustomCampaignNavigationButton icon={<MailOutline />} label="email A" />
            <CustomCampaignNavigationButton icon={<MailOutline />} label="email B" />
            {hasRemainingSend && <CustomCampaignNavigationButton icon={<MailOutline />} label="remaining" />}
        </>
    );
};

const RegularCampaignNavigation = ({ campaign }: { campaign: Campaign }) => {
    const inferredSends = useMemo(() => inferExitSendParentIds(campaign.sends ?? []), [campaign.sends]);
    const groupedSends = useMemo(() => groupSendsByParentId(inferredSends), [inferredSends]);

    const renderSend = (send: Send) => {
        const { channel } = send;
        const Icon = channelIcons[channel];
        const label = send?.splitAction === SplitActions.EXIT ? 'exit' : CampaignChannelLabels[channel];

        return <CustomCampaignNavigationButton icon={<Icon />} label={label} />;
    };

    const renderSendGroup = (
        send: Send,
        children: Send[],
        index: number,
        visitedIds: Set<string | number> = new Set()
    ): ReactNode => {
        const nodeKey = send?.id ?? `idx-${index}`;
        if (visitedIds.has(nodeKey)) return null;
        const nextVisited = new Set(visitedIds);
        nextVisited.add(nodeKey);

        // The first send cannot be time delayed and we do not need to indicate that there's a time
        // delay on the second action in a split. The delay is indicated before the split details.
        const isTimeDelayedSend = index !== 0 && send?.splitType !== SplitTypes.DOES_NOT_MEET;
        // Presently, all splits begin with a meets criteria action. As such, we want to show
        // the conditional split details before that send.
        const shouldShowConditionalSplitDetails = send?.splitType === SplitTypes.MEETS;

        return (
            <Fragment key={`send-${send.id || index}`}>
                {isTimeDelayedSend && <CustomCampaignNavigationButton icon={<AccessTime />} label="time delay" />}
                {shouldShowConditionalSplitDetails && (
                    <CustomCampaignNavigationButton icon={<SwapCalls />} label="conditional split" />
                )}
                {send?.splitType && <div>{send.splitType}</div>}
                {renderSend(send)}

                {/* Recursively render children */}
                {children.map((child) => {
                    const childGroup = groupedSends.find((g) => g.parent === child);
                    const childIndex = childGroup?.parentIndex ?? inferredSends.indexOf(child);
                    const grandchildren = childGroup?.children || [];
                    return renderSendGroup(child, grandchildren, childIndex, nextVisited);
                })}
            </Fragment>
        );
    };

    return (
        <>
            <CustomCampaignNavigationButton icon={<SupervisedUserCircleOutlined />} label="audience" />
            {groupedSends
                .filter((group) => !group.parent.parentId || !inferredSends.some((s) => s.id === group.parent.parentId))
                .map((group) => renderSendGroup(group.parent, group.children, group.parentIndex))}
        </>
    );
};

const LegacyCustomCampaignDetailsNavigation = ({ campaign, isPreviewOpen }: Omit<Props, 'hasNestedSplitsFlag'>) => {
    const isABTest = Boolean(campaign.abTestConfig);

    return (
        <Box display={isPreviewOpen ? 'none' : 'block'} ml={3} height="100%" width="75%">
            {isABTest ? <ABTestNavigation campaign={campaign} /> : <RegularCampaignNavigation campaign={campaign} />}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// New (flag ON): reuses the builder's CustomCampaignNavigation so the summary
// matches the builder exactly — template/audience names, time-delay durations,
// green accent lines, and bordered (nested) conditional-split boxes.
// ---------------------------------------------------------------------------

// Map published Send field names to the draft CampaignSend shapes expected by
// CustomCampaignNavigationSend: emailTemplateId→templateId, *ContentId→content.{id,name}.
// splitNodeId is synthesized first so nested conditional splits are detected.
const adaptSends = (rawSends: Send[]): CampaignSend[] => {
    const sends = synthesizeSplitNodeIds(inferExitSendParentIds(rawSends));
    const idToIndex = new Map(sends.map((s, i) => [s.id, i]));
    return (sends.map((send) => {
        const base = {
            ...send,
            parentIndex: send.parentId != null ? idToIndex.get(send.parentId) ?? null : null,
        };
        switch (send.channel) {
            case CampaignChannels.EMAIL:
                return { ...base, templateId: send.emailTemplateId };
            case CampaignChannels.SMS:
                return { ...base, content: { id: send.smsContentId, name: send.name } };
            case CampaignChannels.DIRECT_MAIL:
                return { ...base, content: { id: send.directMailContentId, name: send.name } };
            case CampaignChannels.VOICE_CALL:
                return { ...base, content: { id: send.voiceContentId, name: send.name } };
            default:
                return base;
        }
    }) as unknown) as CampaignSend[];
};

const NestedSplitCustomCampaignDetailsNavigation = ({
    campaign,
    isPreviewOpen,
}: Omit<Props, 'hasNestedSplitsFlag'>) => {
    const navState = useMemo<CampaignStateTypes>(
        () => ({
            name: campaign.name,
            type: campaign.type as CampaignType,
            sends: adaptSends(campaign.sends ?? []),
            abTestConfig: campaign.abTestConfig ?? undefined,
        }),
        [campaign]
    );

    return (
        <Box display={isPreviewOpen ? 'none' : 'block'} ml={3} height="100%" width="75%">
            <CustomCampaignNavigation
                activeSendIndex={-1}
                activeStep={CustomCampaignSteps.NAME_CAMPAIGN}
                campaignState={navState}
                onRemoveClick={() => {}}
                setActiveSendIndex={() => {}}
                setActiveStep={() => {}}
            />
        </Box>
    );
};

const CustomCampaignDetailsNavigation = ({ campaign, isPreviewOpen, hasNestedSplitsFlag = false }: Props) =>
    hasNestedSplitsFlag ? (
        <NestedSplitCustomCampaignDetailsNavigation campaign={campaign} isPreviewOpen={isPreviewOpen} />
    ) : (
        <LegacyCustomCampaignDetailsNavigation campaign={campaign} isPreviewOpen={isPreviewOpen} />
    );

export default CustomCampaignDetailsNavigation;
